import {
  MANUAL_NOTE_MAX,
  PHOTO_MAX_BYTES,
  clampManualNote,
  isManualLineCode,
  isManualSellerNumber,
  manualLineCode,
  manualLineTitle,
  type ManualLinePayload,
} from '@barso/shared';
import { MAX_PRICE_CENTS, MIN_PRICE_CENTS } from './cart';
import type { CartLine } from './types';

/**
 * Artikel ohne Etikett (U17h, Plan 16 Abschnitt 4.10.9): reine Funktionen. Die Zeile hat KEINEN Etikettencode (Regel 4), nur die
 * lokale Kennung `manual:…` (≤ 30 Zeichen), damit der Warenkorb sie von anderen Zeilen unterscheiden kann. Der Preis ist ganze
 * Cent wie überall (Regel 3).
 */

export type ManualLineError = 'price' | 'seller' | 'photo';

/**
 * Legt die Zeile an. `uuid` macht die Kennung eindeutig (zwei manuelle Zeilen im Korb kommen sich nie in die Quere).
 * Gibt einen Fehlerschlüssel zurück, wenn Preis (0,50 bis 5.000 €), Verkäufernummer oder Foto nicht zulässig sind.
 */
export function makeManualLine(input: {
  uuid: string;
  priceCents: number;
  sellerNumber?: number | undefined;
  note?: string | undefined;
  photoId?: string | undefined;
}): CartLine | ManualLineError {
  if (
    !Number.isInteger(input.priceCents) ||
    input.priceCents < MIN_PRICE_CENTS ||
    input.priceCents > MAX_PRICE_CENTS
  ) {
    return 'price';
  }
  if (input.sellerNumber !== undefined && !isManualSellerNumber(input.sellerNumber))
    return 'seller';
  const note = clampManualNote(input.note);
  const sellerNumber = input.sellerNumber;
  return {
    code: manualLineCode(input.uuid),
    priceCents: input.priceCents,
    itemId: null,
    title: manualLineTitle(sellerNumber),
    sellerNumber: sellerNumber ?? null,
    size: null,
    override: 'unknown',
    manual: {
      ...(sellerNumber === undefined ? {} : { sellerNumber }),
      ...(note === undefined ? {} : { note }),
      ...(input.photoId === undefined ? {} : { photoId: input.photoId }),
    },
  };
}

export const isManualLineError = (value: CartLine | ManualLineError): value is ManualLineError =>
  typeof value === 'string';

/** Ist die Zeile ein Artikel ohne Etikett? */
export const isManualLine = (line: Pick<CartLine, 'code' | 'manual'>): boolean =>
  line.manual !== undefined && isManualLineCode(line.code);

/** Angabe für das Protokoll, nur wenn der Server `manualLines` meldete (sonst geht die Zeile als unbekannter Code mit Preis). */
export function manualPayload(line: CartLine, capable: boolean): ManualLinePayload | undefined {
  if (!capable || line.manual === undefined || !isManualLineCode(line.code)) return undefined;
  const note = clampManualNote(line.manual.note);
  return {
    ...(line.manual.sellerNumber === undefined ? {} : { sellerNumber: line.manual.sellerNumber }),
    ...(note === undefined ? {} : { note: note.slice(0, MANUAL_NOTE_MAX) }),
    ...(line.manual.photoId === undefined ? {} : { photoId: line.manual.photoId }),
  };
}

/** Größe, ab der ein Foto nicht gespeichert wird (der Server nähme es nicht an). */
export const PHOTO_STORE_MAX_BYTES = PHOTO_MAX_BYTES;
